import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../../hooks/useDebounce";
import useI18n from "../../../../../../hooks/useTranslations";
import { SearchAdminItem } from "../../../../../../http/types/items";
import { useItemsApi } from "../../../../../../http/useItems";
import { Handle } from "../../../../../../http/vertigrip/types/handles";
import { AdminInstallationType } from "../../../../../../http/vertigrip/types/installationTypes";
import { useAbsorbersApi } from "../../../../../../http/vertigrip/useAbsorbers";
import Button from "../../../../../ui/Button";
import Form from "../../../../../ui/Form";
import FormField from "../../../../../ui/FormField";
import FormFieldsContainer from "../../../../../ui/FormFieldsContainer";
import ModalDialog from "../../../../../ui/ModalDialog";
import Numeric from "../../../../../ui/Numeric";
import VisibilityScope, { VisibilityScopeValue } from "../../../../../ui/VisibilityScope";
import { CustomAsyncSelect } from "../../../../../ui/select/CustomSelect";

interface CreateAbsorberForm {
  item: SearchAdminItem | null;
  installationType: AdminInstallationType | null;
  handles: Handle[] | null;
  order: number | null;
  visibility: VisibilityScopeValue;
}

const CreateAbsorberModalDialogButton: FC = () => {
  const { steelVersionId } = useParams<{ steelVersionId: string }>();
  const i18n = useI18n();
  const [showNewItemDialog, setShowNewItemDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const itemsApi = useItemsApi();
  const absorbersApi = useAbsorbersApi();
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);

  return (
    <>
      <Button buttonProps={{ onClick: () => setShowNewItemDialog(true) }}>{i18n.translation.common.createNew}</Button>
      {showNewItemDialog && (
        <ModalDialog onClose={() => setShowNewItemDialog(false)} title={i18n.translation.absorbers.createNew}>
          <Form<CreateAbsorberForm>
            defaultValues={{ item: null, order: 50, visibility: "hidden" }}
            submitText={i18n.translation.common.save}
            disabled={isSubmitting}
            secondaryButton={
              <Button buttonProps={{ onClick: () => setShowNewItemDialog(false) }} type="secondary">
                {i18n.translation.common.cancel}
              </Button>
            }
            onSubmit={() => (formData) =>
              absorbersApi
                .insertAdminAbsorber({
                  itemId: formData.item?.id!,
                  order: formData.order!,
                  steelVersionId: steelVersionId!,
                  visibility: formData.visibility,
                })
                .then(() => setShowNewItemDialog(false))
                .catch((err) => {
                  dispatch(showErrorNotification(i18n, err));
                  setIsSubmitting(false);
                })
            }
          >
            {({ control, setValue }) => {
              return (
                <FormFieldsContainer>
                  <Controller
                    name="item"
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => {
                      return (
                        <>
                          <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                            {({ labelId }) => {
                              const searchTerm = useRef("");
                              const aborter = useRef(new AbortController());

                              const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                              // Fetch initial data on mount
                              useEffect(() => {
                                loadOptions(searchTerm.current).then(setDefaultOptions);
                              }, []);

                              return (
                                <CustomAsyncSelect
                                  {...field}
                                  inputId={labelId}
                                  defaultOptions={defaultOptions}
                                  loadOptions={loadOptions}
                                  isClearable
                                  getOptionLabel={(x) => x.code}
                                  getOptionValue={(x) => x.id}
                                  onChange={(value) => setValue("item", value ?? null)}
                                />
                              );
                            }}
                          </FormField>
                        </>
                      );
                    }}
                  />
                  <Controller
                    name="visibility"
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                        {({ labelId }) => (
                          <VisibilityScope
                            {...field}
                            inputId={labelId}
                            onChange={(value) => setValue("visibility", value)}
                          />
                        )}
                      </FormField>
                    )}
                  />
                  <Controller
                    name="order"
                    control={control}
                    rules={{ required: true, min: 1 }}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.translation.common.order} error={fieldState.error}>
                        {({ labelId, isInvalid }) => (
                          <Numeric
                            {...field}
                            id={labelId}
                            isInvalid={isInvalid}
                            decimalScale={0}
                            allowNegative={false}
                            onBlur={(value) => setValue("order", value)}
                          />
                        )}
                      </FormField>
                    )}
                  />
                </FormFieldsContainer>
              );
            }}
          </Form>
        </ModalDialog>
      )}
    </>
  );
};

export default CreateAbsorberModalDialogButton;
